<script setup lang="ts">
import {
  Bookmark as BookmarkIcon,
  Globe as GlobeIcon,
  Image as ImageIcon,
  Users as UsersIcon,
  Waypoints as WaypointsIcon,
} from '@lucide/vue'
import { nextTick, ref, watch } from 'vue'

import type { AddToCollectionTarget } from '@/services/addToCollectionService'

defineOptions({ name: 'AddToCollectionPickerContent' })

const props = defineProps<{
  workspaces: AddToCollectionTarget[]
  addingWorkspaceId?: string | null
  creating: boolean
  draftName: string
}>()

const emit = defineEmits<{
  select: [workspace: AddToCollectionTarget]
  'update:draftName': [value: string]
}>()

const draftInputRef = ref<HTMLInputElement | null>(null)

watch(
  () => props.creating,
  async (creating) => {
    if (!creating) return
    await nextTick()
    draftInputRef.value?.focus()
    draftInputRef.value?.select()
  },
)

const placeholderStyle = (workspaceId: string) => {
  let hash = 0
  for (let i = 0; i < workspaceId.length; i += 1) {
    hash = (hash * 31 + workspaceId.charCodeAt(i)) % 360
  }
  return {
    background: `linear-gradient(135deg, hsl(${hash} 60% 88%), hsl(${(hash + 28) % 360} 58% 76%))`,
    color: `hsl(${hash} 38% 28%)`,
  }
}
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col overflow-y-auto py-1">
    <div
      v-if="creating"
      class="flex w-full items-center gap-3 px-4 py-2.5 text-left"
      data-testid="add-to-collection-new-row"
    >
      <div
        class="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-emerald-100 bg-emerald-50 text-emerald-700"
      >
        <ImageIcon class="h-5 w-5" />
      </div>
      <input
        ref="draftInputRef"
        :value="draftName"
        class="min-w-0 flex-1 rounded-md border border-emerald-300 bg-white px-2 py-1 text-base font-semibold text-gray-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
        aria-label="New collection name"
        @input="emit('update:draftName', ($event.target as HTMLInputElement).value)"
      />
    </div>

    <button
      v-for="workspace in workspaces"
      :key="workspace.clientId"
      type="button"
      class="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-gray-50 disabled:cursor-wait disabled:opacity-60"
      :disabled="addingWorkspaceId === workspace.clientId"
      @click="emit('select', workspace)"
    >
      <div class="relative h-14 w-14 shrink-0 overflow-hidden rounded-md">
        <img
          v-if="workspace.coverPhotoThumbnailUrl"
          :src="workspace.coverPhotoThumbnailUrl"
          class="h-full w-full object-cover"
          alt=""
        />
        <div
          v-else
          class="flex h-full w-full items-center justify-center"
          :style="placeholderStyle(workspace.clientId)"
        >
          <WaypointsIcon class="h-6 w-6" />
        </div>
      </div>

      <div class="min-w-0 flex-1">
        <div class="flex min-w-0 items-center gap-1.5">
          <span class="truncate text-base font-semibold text-gray-900">
            {{ workspace.name }}
          </span>
          <UsersIcon
            v-if="workspace.userRole && !workspace.isOwner"
            class="h-3.5 w-3.5 shrink-0 text-gray-900"
            title="Shared with you"
          />
          <GlobeIcon
            v-if="!workspace.isBookmarked && workspace.isPublic"
            class="h-3.5 w-3.5 shrink-0 text-gray-900"
            title="Public collection"
          />
          <BookmarkIcon
            v-if="workspace.isBookmarked"
            class="h-3.5 w-3.5 shrink-0 text-gray-900"
            title="Saved collection"
          />
        </div>
      </div>
    </button>

    <p
      v-if="workspaces.length === 0 && !creating"
      class="px-4 py-6 text-center text-base text-gray-500"
    >
      No editable collections found.
    </p>
  </div>
</template>
